import React, { useEffect } from "react";
import classNames from "classnames";
import { CSSTransition } from "react-transition-group";
import * as math from "mathjs";
import { Link, useSearchParams } from "react-router-dom";

import Input from "components/input";
import Button from "components/button";
import ButtonLoader from "components/button-loader";
import Icon from "icons";
import request from "request";
import modalStyles from "./components/create-modal.module.scss";
import Modal from "./components/create-modal";
import styles from "./home.module.scss";
import home from "./home";
import { getOcrStatusClassName, getOcrStatusText } from "hooks";

const firstDayOfYear = new Date(new Date().getFullYear(), 0, 1);
const lastDayOfYear = new Date(new Date().getFullYear(), 11, 31);

let debounceTimeout: NodeJS.Timeout;

const Home = () => {
  const [from, setFrom] = React.useState<Date>(firstDayOfYear);
  const [to, setTo] = React.useState<Date>(lastDayOfYear);
  const [page, setPage] = React.useState(1);
  const [rowCount, setRowCount] = React.useState(1);
  const [newApplicationModal, setNewApplicationModal] = React.useState(false);
  const [pending, setPending] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [exportExcelPending, setExportExcelPending] = React.useState(false);
  const resultRef = React.useRef<HTMLDivElement | null>(null);
  const [params, setParams] = useSearchParams();

  const [data, setData] = React.useState<home.result[]>([]);

  const handleFilter = async (FROM?: Date, TO?: Date) => {
    const fd = (FROM ?? from)
      .toLocaleDateString("en-GB")
      .split("/")
      .reverse()
      .join("-");
    const td = (TO ?? to)
      .toLocaleDateString("en-GB")
      .split("/")
      .reverse()
      .join("-");
    setPending(true);
    setPage(1);
    resultRef.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
    request
      .get(`/applications/history?fd=${fd}&td=${td}&page=${page}`)
      .then((res) => res.data)
      .then((d) => {
        setData(d.data);
        setRowCount(d.total_count);
      })
      .finally(() => setPending(false));
  };

  const handleClear = () => {
    setFrom(firstDayOfYear);
    setTo(lastDayOfYear);
    handleFilter(firstDayOfYear, lastDayOfYear);
  };

  const handleExportExcel = () => {
    const fd = from.toLocaleDateString("en-GB").split("/").reverse().join("-");
    const td = to.toLocaleDateString("en-GB").split("/").reverse().join("-");
    setExportExcelPending(true);
    request({
      method: "POST",
      url: `/applications/xlsx?fd=${fd}&td=${td}`,
      responseType: "blob",
    })
      .then((res) => res.data)
      .then((res) => {
        const href = URL.createObjectURL(res);
        const link = document.createElement("a");
        link.href = href;
        link.setAttribute("download", fd + "," + td + ".xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .finally(() => setExportExcelPending(false));
  };

  React.useEffect(() => {
    setPending(true);
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      const handleLoadRows = () => {
        const fd = from
          .toLocaleDateString("en-GB")
          .split("/")
          .reverse()
          .join("-");
        const td = to
          .toLocaleDateString("en-GB")
          .split("/")
          .reverse()
          .join("-");
        resultRef.current?.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
        request
          .get(
            `/applications/history?fd=${fd}&td=${td}&page=${page}&user_id=${search}`
          )
          .then((res) => res.data)
          .then((d) => {
            setData(d.data);
            setRowCount(d.total_count);
          })
          .finally(() => setPending(false));
      };
      handleLoadRows();
    }, 400);
    return () => clearTimeout(debounceTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  useEffect(() => {
    const paramPage = Number(params.get("page"));
    if (paramPage && paramPage !== null && !isNaN(paramPage)) {
      setPage(Number(paramPage));
    } else {
      setPage(1);
    }
  }, []);

  return (
    <div className={styles.results} ref={resultRef}>
      <div className={styles.container}>
        <div className={styles.tools}>
          <div className={styles.filterBox}>
            <div className={styles.input}>
              <Input
                title="Поиск по ID"
                value={search}
                type="string"
                onInput={(value) => setSearch(value)}
              />
            </div>
          </div>
          <div className={styles.filterBox}>
            <div className={styles.input}>
              <Input
                title="От"
                value={from}
                type="date"
                onSetDate={(d) => setFrom(d)}
              />
            </div>
            <div className={styles.input}>
              <Input
                title="До"
                value={to}
                type="date"
                onSetDate={(d) => setTo(d)}
              />
            </div>
            <div className={styles.buttonGroup}>
              <Button pending={pending} onClick={() => handleFilter()}>
                Фильтр
              </Button>
              <Button pending={pending} color="red" onClick={handleClear}>
                Сбросить
              </Button>
            </div>
          </div>
          <div className={styles.buttonGroup}>
            <Button
              pending={exportExcelPending || pending}
              color="orange"
              onClick={handleExportExcel}
            >
              Экспортировать в .xlsx
            </Button>
            <Button color="green" onClick={() => setNewApplicationModal(true)}>
              Новая заявка
            </Button>
          </div>
        </div>
        {pending && (
          <div className={styles.tableLoading}>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
          </div>
        )}
        {!pending && data && data.length !== 0 && (
          <div className={styles.table}>
            {data.map((item) => {
              return (
                <Link
                  to={`application/${item.id}/ocr/pkb`}
                  className={styles.tableRowWrap}
                  key={item.id}
                  state={{ key: page }}
                >
                  <div className={styles.tableRow}>
                    <div className={styles.tableRowBottom}>
                      <div className={styles.item}>
                        <span>ID</span>
                        <div className={styles.item_value}>{item.user_id}</div>
                      </div>
                      <div className={styles.item}>
                        <span>Эксперт</span>
                        <div className={styles.item_value}>
                          {item?.created_by.email}
                        </div>
                      </div>
                      <div className={styles.item}>
                        <span>Дата создания заявки</span>
                        <div className={styles.item_value}>
                          {new Date(item?.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div
                        className={classNames(
                          styles.item,
                          getOcrStatusClassName(item?.ocr_log, styles)
                        )}
                      >
                        <span>ПКБ:</span>
                        {getOcrStatusText(item?.ocr_log)}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
        {!pending && data.length === 0 && (
          <div className={styles.tableIsEmptyInfo}>Нет заявок</div>
        )}
        {rowCount > 15 && (
          <div className={styles.pagination}>
            <button
              className={classNames(styles.arrow, page <= 1 && styles.disabled)}
              onClick={() => {
                const paramPage = String(math.max(page - 1, 1));
                params.set("page", paramPage);
                setParams(params);
                setPage(math.max(page - 1, 1));
              }}
            >
              <Icon name="arrowLeft" />
            </button>
            <div className={styles.label}>
              {page} / {math.ceil(rowCount / 15)}
            </div>
            <button
              className={classNames(
                styles.arrow,
                page * 15 >= rowCount && styles.disabled
              )}
              onClick={() => {
                const paramPage = String(
                  math.min(page + 1, math.ceil(rowCount / 15))
                );
                params.set("page", paramPage);
                setParams(params);
                setPage(math.min(page + 1, math.ceil(rowCount / 15)));
              }}
            >
              <Icon name="arrowRight" />
            </button>
          </div>
        )}
      </div>
      <CSSTransition
        in={!!newApplicationModal}
        timeout={150}
        unmountOnExit
        classNames={{
          enter: modalStyles.modalEnter,
          enterActive: modalStyles.modalEnterActive,
          exit: modalStyles.modalExit,
          exitActive: modalStyles.modalExitActive,
        }}
      >
        <Modal onClose={() => setNewApplicationModal(false)} />
      </CSSTransition>
    </div>
  );
};

export default Home;
